/* PhotoMarkupEditor — Fabric.js-powered markup tool for images & PDFs.

   Tools: pen / freehand (color + width), text labels (color + font, draggable),
   arrow, rectangle, circle, highlighter (semi-transparent yellow),
   plus undo / redo / clear / delete.

   PDF flow: we render all pages with pdfjs, paint them onto the canvas
   stacked vertically, then let the user draw on top. On save we export a
   single PNG image (one composite page). This keeps the UI predictable
   and the storage path the same. */

import { useEffect, useMemo, useRef, useState } from "react";
import * as fabric from "fabric";
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Pencil, Type, ArrowRight, Square, Circle as CircleIcon, Highlighter,
  Undo2, Redo2, Trash2, Save, MousePointer2, X, Loader2,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const COLORS = ["#ef4444", "#f97316", "#eab308", "#22c55e", "#3b82f6", "#a855f7", "#000000", "#ffffff"];
const FONTS = ["Inter", "Georgia", "Courier New", "Impact", "Comic Sans MS"];

export default function PhotoMarkupEditor({ open, file, onClose, onSaved }) {
  const canvasElRef = useRef(null);
  const canvasRef = useRef(null);             // Fabric canvas instance
  const containerRef = useRef(null);
  const historyRef = useRef({ stack: [], idx: -1, suspend: false });

  const [tool, setTool] = useState("select"); // select | pen | text | arrow | rect | circle | highlight
  const [color, setColor] = useState("#ef4444");
  const [width, setWidth] = useState(4);
  const [font, setFont] = useState("Inter");
  const [fontSize, setFontSize] = useState(28);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [bgInfo, setBgInfo] = useState({ width: 0, height: 0 });

  const isPdf = useMemo(() => {
    const n = (file?.original_name || file?.filename || "").toLowerCase();
    return n.endsWith(".pdf") || (file?.content_type || "").includes("pdf");
  }, [file]);

  const fullUrl = useMemo(() => {
    if (!file?.url) return "";
    if (file.url.startsWith("http")) return file.url;
    return `${process.env.REACT_APP_BACKEND_URL}${file.url}`;
  }, [file]);

  // ----- History (undo / redo) -----
  const snapshot = () => {
    if (!canvasRef.current || historyRef.current.suspend) return;
    const json = canvasRef.current.toJSON(["selectable"]);
    const h = historyRef.current;
    h.stack = h.stack.slice(0, h.idx + 1);
    h.stack.push(JSON.stringify(json));
    h.idx = h.stack.length - 1;
    setCanUndo(h.idx > 0);
    setCanRedo(false);
  };
  const undo = async () => {
    const h = historyRef.current;
    if (h.idx <= 0) return;
    h.idx -= 1;
    h.suspend = true;
    await canvasRef.current.loadFromJSON(JSON.parse(h.stack[h.idx]));
    canvasRef.current.requestRenderAll();
    h.suspend = false;
    setCanUndo(h.idx > 0);
    setCanRedo(h.idx < h.stack.length - 1);
  };
  const redo = async () => {
    const h = historyRef.current;
    if (h.idx >= h.stack.length - 1) return;
    h.idx += 1;
    h.suspend = true;
    await canvasRef.current.loadFromJSON(JSON.parse(h.stack[h.idx]));
    canvasRef.current.requestRenderAll();
    h.suspend = false;
    setCanUndo(h.idx > 0);
    setCanRedo(h.idx < h.stack.length - 1);
  };

  // ----- Canvas init -----
  useEffect(() => {
    if (!open || !file) return;
    let cancelled = false;
    setLoading(true);

    const init = async () => {
      const c = new fabric.Canvas(canvasElRef.current, {
        backgroundColor: "#ffffff",
        preserveObjectStacking: true,
        selection: true,
      });
      canvasRef.current = c;

      // Load background
      let bgWidth = 1000, bgHeight = 700;
      try {
        if (isPdf) {
          const data = await fetch(fullUrl).then(r => r.arrayBuffer());
          const pdf = await pdfjsLib.getDocument({ data }).promise;
          const pageImgs = [];
          let totalH = 0;
          let maxW = 0;
          for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const vp = page.getViewport({ scale: 2 });
            const offscreen = document.createElement("canvas");
            offscreen.width = vp.width;
            offscreen.height = vp.height;
            await page.render({ canvasContext: offscreen.getContext("2d"), viewport: vp, canvas: offscreen }).promise;
            pageImgs.push({ canvas: offscreen, w: vp.width, h: vp.height });
            totalH += vp.height + 16;
            maxW = Math.max(maxW, vp.width);
          }
          // Stitch into one tall canvas
          const stitched = document.createElement("canvas");
          stitched.width = maxW;
          stitched.height = totalH;
          const ctx = stitched.getContext("2d");
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, stitched.width, stitched.height);
          let y = 0;
          for (const pg of pageImgs) {
            ctx.drawImage(pg.canvas, (maxW - pg.w) / 2, y);
            y += pg.h + 16;
          }
          bgWidth = stitched.width;
          bgHeight = stitched.height;
          const bgImg = new fabric.FabricImage(stitched, { selectable: false, evented: false });
          c.set({ width: bgWidth, height: bgHeight });
          c.backgroundImage = bgImg;
        } else {
          const img = await fabric.FabricImage.fromURL(fullUrl, { crossOrigin: "anonymous" });
          bgWidth = img.width || 1000;
          bgHeight = img.height || 700;
          img.set({ selectable: false, evented: false });
          c.set({ width: bgWidth, height: bgHeight });
          c.backgroundImage = img;
        }
      } catch (e) {
        console.error("Background load failed", e);
        toast.error("Could not load file for editing");
      }

      if (cancelled) {
        c.dispose();
        return;
      }
      setBgInfo({ width: bgWidth, height: bgHeight });
      c.requestRenderAll();
      fitToContainer(c, bgWidth, bgHeight);

      c.on("object:added", snapshot);
      c.on("object:modified", snapshot);
      c.on("path:created", snapshot);
      snapshot(); // initial

      setLoading(false);
    };

    init();

    return () => {
      cancelled = true;
      if (canvasRef.current) {
        try { canvasRef.current.dispose(); } catch { /* noop */ }
        canvasRef.current = null;
      }
      historyRef.current = { stack: [], idx: -1, suspend: false };
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, file?.id]);

  // Fit canvas inside the modal container while keeping the underlying
  // pixel resolution intact (so saved output stays high-res).
  const fitToContainer = (c, w, h) => {
    if (!containerRef.current) return;
    const maxW = containerRef.current.clientWidth - 32;
    const maxH = window.innerHeight - 280;
    const scale = Math.min(maxW / w, maxH / h, 1);
    c.setDimensions({ width: w * scale, height: h * scale }, { cssOnly: true });
  };
  useEffect(() => {
    const onResize = () => {
      if (canvasRef.current && bgInfo.width) {
        fitToContainer(canvasRef.current, bgInfo.width, bgInfo.height);
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [bgInfo]);

  // ----- Tool state -----
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    c.isDrawingMode = tool === "pen" || tool === "highlight";
    c.selection = tool === "select";
    if (c.isDrawingMode) {
      const brush = new fabric.PencilBrush(c);
      brush.color = tool === "highlight" ? hexToRgba(color === "#ffffff" ? "#fde047" : color, 0.35) : color;
      brush.width = tool === "highlight" ? Math.max(width * 4, 18) : width;
      c.freeDrawingBrush = brush;
    }
    // Update cursor
    c.defaultCursor = tool === "select" ? "default" : "crosshair";
  }, [tool, color, width]);

  // Click-to-place for shapes / text / arrow
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const handler = (opt) => {
      if (tool === "select" || tool === "pen" || tool === "highlight") return;
      const p = c.getViewportPoint(opt.e);
      // Convert viewport point back into the canvas coord system
      const pointer = c.getScenePoint(opt.e);
      let obj;
      if (tool === "text") {
        obj = new fabric.IText("Text", {
          left: pointer.x, top: pointer.y,
          fontFamily: font, fontSize, fill: color,
          editable: true,
        });
        c.add(obj);
        c.setActiveObject(obj);
        obj.enterEditing();
        obj.selectAll();
      } else if (tool === "rect") {
        obj = new fabric.Rect({
          left: pointer.x, top: pointer.y,
          width: 140, height: 90,
          fill: "transparent", stroke: color, strokeWidth: width,
        });
        c.add(obj);
        c.setActiveObject(obj);
      } else if (tool === "circle") {
        obj = new fabric.Ellipse({
          left: pointer.x, top: pointer.y,
          rx: 70, ry: 70,
          fill: "transparent", stroke: color, strokeWidth: width,
        });
        c.add(obj);
        c.setActiveObject(obj);
      } else if (tool === "arrow") {
        obj = makeArrow(pointer.x - 80, pointer.y, pointer.x + 80, pointer.y, color, width);
        c.add(obj);
        c.setActiveObject(obj);
      }
      // Snap back to select after placing (lets user immediately reposition)
      if (obj) setTool("select");
    };
    c.on("mouse:down", handler);
    return () => c.off("mouse:down", handler);
  }, [tool, color, width, font, fontSize]);

  // ----- Actions -----
  const deleteSelected = () => {
    const c = canvasRef.current;
    if (!c) return;
    const objs = c.getActiveObjects();
    objs.forEach(o => c.remove(o));
    c.discardActiveObject();
    c.requestRenderAll();
    snapshot();
  };

  const clearAll = () => {
    const c = canvasRef.current;
    if (!c) return;
    c.getObjects().slice().forEach(o => c.remove(o));
    c.requestRenderAll();
    snapshot();
  };

  const handleSave = async () => {
    const c = canvasRef.current;
    if (!c || !file) return;
    setSaving(true);
    try {
      // Export at native resolution by temporarily resetting the css size
      const dataUrl = c.toDataURL({ format: "png", multiplier: 1 });
      const blob = await (await fetch(dataUrl)).blob();
      const fd = new FormData();
      const ext = isPdf ? ".png" : ".png"; // always export PNG for annotated images
      fd.append("file", blob, `annotated-${file.id}${ext}`);
      const res = await axios.post(`${API_URL}/uploads/${file.id}/annotate`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Markup saved");
      onSaved && onSaved(res.data);
      onClose && onClose();
    } catch (e) {
      console.error(e);
      toast.error("Could not save markup");
    } finally {
      setSaving(false);
    }
  };

  if (!file) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose && onClose(); }}>
      <DialogContent className="max-w-6xl p-0 overflow-hidden" data-testid="photo-markup-editor">
        <DialogHeader className="px-4 pt-4 pb-2 border-b">
          <div className="flex items-center justify-between gap-3">
            <DialogTitle className="text-base truncate">Markup · {file.original_name}</DialogTitle>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" size="sm" onClick={onClose} data-testid="markup-cancel">Cancel</Button>
              <Button type="button" size="sm" onClick={handleSave} disabled={saving || loading} className="bg-[#0F4C81] hover:bg-[#0F4C81]/90" data-testid="markup-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                Save markup
              </Button>
            </div>
          </div>
        </DialogHeader>

        {/* Toolbar */}
        <div className="px-3 py-2 border-b bg-slate-50 flex flex-wrap items-center gap-2">
          <ToolBtn active={tool === "select"} onClick={() => setTool("select")} icon={MousePointer2} label="Select" testid="tool-select" />
          <ToolBtn active={tool === "pen"} onClick={() => setTool("pen")} icon={Pencil} label="Pen" testid="tool-pen" />
          <ToolBtn active={tool === "highlight"} onClick={() => setTool("highlight")} icon={Highlighter} label="Highlight" testid="tool-highlight" />
          <ToolBtn active={tool === "text"} onClick={() => setTool("text")} icon={Type} label="Text" testid="tool-text" />
          <ToolBtn active={tool === "arrow"} onClick={() => setTool("arrow")} icon={ArrowRight} label="Arrow" testid="tool-arrow" />
          <ToolBtn active={tool === "rect"} onClick={() => setTool("rect")} icon={Square} label="Square" testid="tool-rect" />
          <ToolBtn active={tool === "circle"} onClick={() => setTool("circle")} icon={CircleIcon} label="Circle" testid="tool-circle" />

          <div className="h-6 w-px bg-slate-300 mx-1" />

          {/* Color swatches */}
          <div className="flex items-center gap-1">
            {COLORS.map(c => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                className={`w-6 h-6 rounded-full border-2 ${color === c ? "border-[#0F4C81] ring-2 ring-[#0F4C81]/30" : "border-slate-300"}`}
                style={{ background: c }}
                title={c}
                data-testid={`color-${c.replace('#', '')}`}
              />
            ))}
          </div>

          <div className="h-6 w-px bg-slate-300 mx-1" />

          {/* Thickness */}
          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-500">Size</Label>
            <input
              type="range" min="1" max="40" value={width}
              onChange={(e) => setWidth(parseInt(e.target.value))}
              className="w-24 accent-[#0F4C81]"
              data-testid="tool-width"
            />
            <span className="text-xs text-slate-500 w-6 text-right">{width}</span>
          </div>

          {tool === "text" && (
            <>
              <div className="h-6 w-px bg-slate-300 mx-1" />
              <select value={font} onChange={(e) => setFont(e.target.value)} className="text-xs border rounded px-2 py-1" data-testid="tool-font">
                {FONTS.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
              <input
                type="number" min="10" max="120" value={fontSize}
                onChange={(e) => setFontSize(parseInt(e.target.value) || 12)}
                className="w-14 text-xs border rounded px-2 py-1"
                data-testid="tool-font-size"
              />
            </>
          )}

          <div className="h-6 w-px bg-slate-300 mx-1" />

          {/* History */}
          <Button type="button" variant="ghost" size="sm" onClick={undo} disabled={!canUndo} data-testid="tool-undo"><Undo2 className="w-4 h-4" /></Button>
          <Button type="button" variant="ghost" size="sm" onClick={redo} disabled={!canRedo} data-testid="tool-redo"><Redo2 className="w-4 h-4" /></Button>
          <Button type="button" variant="ghost" size="sm" onClick={deleteSelected} className="text-rose-600 hover:text-rose-700" data-testid="tool-delete-selected"><X className="w-4 h-4" /></Button>
          <Button type="button" variant="ghost" size="sm" onClick={clearAll} className="text-rose-600 hover:text-rose-700" data-testid="tool-clear"><Trash2 className="w-4 h-4" /></Button>
        </div>

        {/* Canvas surface */}
        <div ref={containerRef} className="relative bg-slate-100 flex items-center justify-center overflow-auto" style={{ minHeight: 360, maxHeight: "calc(100vh - 220px)" }}>
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/70 z-10">
              <Loader2 className="w-6 h-6 animate-spin text-[#0F4C81]" />
              <span className="ml-2 text-sm text-slate-600">Loading…</span>
            </div>
          )}
          <div className="my-4 shadow border border-slate-200 bg-white">
            <canvas ref={canvasElRef} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ToolBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium border transition-colors ${active ? "bg-[#0F4C81] text-white border-[#0F4C81]" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
    >
      <Icon className="w-3.5 h-3.5" />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

function makeArrow(x1, y1, x2, y2, color, w) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const angle = Math.atan2(dy, dx);
  const headLen = Math.max(14, w * 4);
  const line = new fabric.Line([x1, y1, x2, y2], {
    stroke: color, strokeWidth: w, strokeLineCap: "round", originX: "center", originY: "center",
  });
  const head = new fabric.Triangle({
    left: x2,
    top: y2,
    originX: "center",
    originY: "center",
    width: headLen,
    height: headLen,
    fill: color,
    angle: (angle * 180) / Math.PI + 90,
  });
  const group = new fabric.Group([line, head], { hasBorders: true, hasControls: true });
  return group;
}

function hexToRgba(hex, alpha) {
  const m = hex.replace("#", "");
  const bigint = parseInt(m.length === 3 ? m.split("").map(c => c + c).join("") : m, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
